import React, { useState, cloneElement } from 'react';
import { Link } from 'react-router-dom';
import { BookIcon, PenToolIcon, BarChartIcon, HeartIcon, LightbulbIcon, CheckIcon, XIcon, ArrowRightIcon, MessageSquareIcon, StarIcon, UsersIcon, ClockIcon, DollarSignIcon, BrainIcon, InfoIcon, AlertCircleIcon, TrendingUpIcon, MapIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useUserRole } from '../../context/UserRoleContext';
import { TroveLayout } from '../../components/Trove/TroveLayout';
const StoryInYourHead = () => {
  const {
    darkMode
  } = useDarkMode();
  const {
    isReader
  } = useUserRole();
  const [activeTab, setActiveTab] = useState('what-is');
  const activeTabClass = isReader ? 'text-indigo-600 border-indigo-600' : 'text-purple-600 border-purple-600';
  const activeIconClass = isReader ? 'text-indigo-600' : 'text-purple-600';
  const hoverTextClass = isReader ? 'hover:text-indigo-600' : 'hover:text-purple-600';
  const buttonBg = isReader ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-purple-600 hover:bg-purple-700';
  const accentText = isReader ? 'text-indigo-600' : 'text-purple-600';
  const accentBgLight = isReader ? 'bg-indigo-100' : 'bg-purple-100';
  const accentBgLighter = isReader ? 'bg-indigo-50' : 'bg-purple-50';
  const accentBorder = isReader ? 'border-indigo-200' : 'border-purple-200';
  const accentIconBg = isReader ? 'bg-indigo-900/50' : 'bg-purple-900/50';
  const tabs = [{
    id: 'what-is',
    label: 'What is Self-Publishing?',
    icon: <BookIcon className="h-5 w-5" data-id="element-16691" data-mp="17rh0pvz0lyue" />
  }, {
    id: 'comparison',
    label: 'Publishing Models',
    icon: <BarChartIcon className="h-5 w-5" data-id="element-16692" data-mp="xzkv7zuln6g5" />
  }, {
    id: 'right-for-you',
    label: 'Is It Right for You?',
    icon: <HeartIcon className="h-5 w-5" data-id="element-16693" data-mp="jzjr8bj1fiwg" />
  }, {
    id: 'stories',
    label: 'Author Stories',
    icon: <MessageSquareIcon className="h-5 w-5" data-id="element-16694" data-mp="oxv3eswt25hu" />
  }, {
    id: 'start-here',
    label: 'Start Here',
    icon: <LightbulbIcon className="h-5 w-5" data-id="element-16695" data-mp="2qpn31a6tt6w3" />
  }];
  const ProsConsItem = ({
    type,
    text
  }: {
    type: 'pro' | 'con';
    text: string;
  }) => <div className="flex items-start mb-3" data-id="element-16696" data-mp="2hv8qvghlech7">
      <div className={`flex-shrink-0 h-5 w-5 rounded-full ${type === 'pro' ? 'bg-green-100' : 'bg-red-100'} flex items-center justify-center mr-3 mt-0.5`} data-id="element-16697" data-mp="2hwa6ly9jzn59">
        {type === 'pro' ? <CheckIcon className="h-3 w-3 text-green-600" data-id="element-16698" data-mp="39m55aljawp6l" /> : <XIcon className="h-3 w-3 text-red-600" data-id="element-16699" data-mp="35me8a036lctm" />}
      </div>
      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} text-sm`} data-id="element-16700" data-mp="30lzxwbe3xqsf">
        {text}
      </p>
    </div>;
  const MythCard = ({
    myth,
    reality
  }: {
    myth: string;
    reality: string;
  }) => <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border overflow-hidden`} data-id="element-16701" data-mp="1p6tgj79eoic7">
      <div className={`px-4 py-3 ${darkMode ? 'bg-red-900/20 border-red-900/40' : 'bg-red-50 border-red-100'} border-b flex items-start gap-2`} data-id="element-16702" data-mp="1k78bqg7k1frk">
        <XIcon className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" data-id="element-16703" data-mp="1f2mr22kvvaem" />
        <p className={`text-sm font-medium ${darkMode ? 'text-red-300' : 'text-red-700'}`} data-id="element-16704" data-mp="1f0tighqpmg3z">
          {myth}
        </p>
      </div>
      <div className="px-4 py-3 flex items-start gap-2" data-id="element-16705" data-mp="1ou0vmoroz0em">
        <CheckIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" data-id="element-16706" data-mp="1suksdr8cs208" />
        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-16707" data-mp="1swnieb9i9v4y">
          {reality}
        </p>
      </div>
    </div>;
  const AuthorSpotlight = ({
    name,
    book,
    image,
    story,
    outcome
  }: {
    name: string;
    book: string;
    image: string;
    story: string;
    outcome: string;
  }) => <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-sm border p-5 mb-4`} data-id="element-16708" data-mp="8fhgw5yht2hk">
      <div className="flex items-center mb-4" data-id="element-16709" data-mp="de2fk0y3je3w">
        <div className="w-14 h-14 rounded-full overflow-hidden mr-4 flex-shrink-0" data-id="element-16710" data-mp="23ueglke9sl9k">
          <img src={image} alt={name} className="w-full h-full object-cover" data-id="element-16711" data-mp="1yw38be3miitg" />
        </div>
        <div data-id="element-16712" data-mp="2cvm2f2zlntmr">
          <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16713" data-mp="2cw4aqxzjdyb1">
            {name}
          </h4>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16714" data-mp="27x16hfrdrw44">
            Author of "{book}"
          </p>
        </div>
      </div>
      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} text-sm mb-3 italic leading-relaxed`} data-id="element-16715" data-mp="1g6d2lvybocmr">
        "{story}"
      </p>
      <div className={`${darkMode ? 'bg-gray-700/50' : accentBgLighter} rounded-md p-3`} data-id="element-16716" data-mp="1tyl0jr1ce9n1">
        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-16717" data-mp="11gfl1yuwel6j">
          {outcome}
        </p>
      </div>
    </div>;
  const cardClass = `${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg shadow-sm border p-6 mb-8`;
  const headingClass = `text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`;
  const subHeadingClass = `text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`;
  const textClass = `${darkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const subtleText = `${darkMode ? 'text-gray-400' : 'text-gray-500'}`;
  const myths = [{
    myth: 'My book is unique — there is nothing else like it out there.',
    reality: "Every story draws on universal themes. That's not a weakness — it's how stories connect with readers. The question isn't whether your idea is original; it's whether you can execute it well and find the readers who'll love it."
  }, {
    myth: 'The market is too saturated. There is no room for new authors.',
    reality: "Readers finish books and immediately want more. The market isn't saturated for good books in the right genre — it's saturated with books that don't reach their audience. Discoverability is the real challenge, not competition."
  }, {
    myth: 'I just need one viral moment and everything will take off.',
    reality: "Viral success is real but rare, and it's not a strategy. Most authors who earn meaningful income do so through multiple books, consistent marketing, and years of steady effort — not a single break."
  }, {
    myth: 'Once my book is published, sales will come.',
    reality: "Publishing is the beginning, not the finish line. A book with no existing audience and no marketing plan will likely sell to friends and family — and that's it. Not because the book is bad, but because no one knows it exists."
  }];
  const renderTabContent = () => {
    switch (activeTab) {
      case 'what-is':
        return <div data-id="element-16718" data-mp="2tyealosba2ip">
            <h2 className={headingClass} data-id="element-16719" data-mp="2twti04pk4tnh">What is Self-Publishing?</h2>
            <p className={`${subtleText} text-sm mb-8`} data-id="element-16720" data-mp="2p0m6zxib0ryo">
              A plain-language overview of what it actually means to publish
              your own book.
            </p>

            <div className={cardClass} data-id="element-16721" data-mp="31hna8qb0mmuk">
              <p className={`${textClass} mb-4 leading-relaxed`} data-id="element-16722" data-mp="31glw2cypdk8m">
                Self-publishing means you take on the role of publisher for your
                own work. Instead of submitting to traditional publishing
                houses, you manage the process from editing and design through
                to distribution and marketing — and you keep the profits.
              </p>
              <p className={`${textClass} leading-relaxed`} data-id="element-16723" data-mp="2ilx5ivbp7ccz">
                In practice, you become a small business owner as well as a
                writer. It's an empowering path that's opened doors for
                countless authors, but it works best when you go in with clear
                expectations.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-16724" data-mp="6tief3501863">
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-5`} data-id="element-16725" data-mp="svkbqo9t53qi">
                <h3 className={`${subHeadingClass} text-base`} data-id="element-16726" data-mp="nyk62egve2zx">
                  What you gain
                </h3>
                <ProsConsItem type="pro" text="Complete creative control — cover, content, pricing, timeline" data-id="element-16727" data-mp="12ngam1jpwxvs" />
                <ProsConsItem type="pro" text="Higher royalty rates: 35–70% vs. 8–15% in traditional publishing" data-id="element-16728" data-mp="1bl0c9blhsw6x" />
                <ProsConsItem type="pro" text="Faster to market — weeks or months, not years" data-id="element-16729" data-mp="1ll111d24s4ii" />
                <ProsConsItem type="pro" text="You retain all rights and can change strategy at any time" data-id="element-16730" data-mp="1gm66xeypqtj3" />
                <ProsConsItem type="pro" text="No gatekeepers — niche and underserved audiences are welcome" data-id="element-16731" data-mp="3idjbg2wrqzem" />
              </div>
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-5`} data-id="element-16732" data-mp="22tk1qjxnphum">
                <h3 className={`${subHeadingClass} text-base`} data-id="element-16733" data-mp="1o4nnu68x5am3">
                  What it costs you
                </h3>
                <ProsConsItem type="con" text="Upfront investment for editing, cover design, and formatting" data-id="element-16734" data-mp="1f48xgsh3l5bq" />
                <ProsConsItem type="con" text="You're responsible for marketing — readers won't find you by accident" data-id="element-16735" data-mp="1j7dxmodpgux7" />
                <ProsConsItem type="con" text="Steep learning curve across publishing, business, and promotion" data-id="element-16736" data-mp="1a4cveyrynnyt" />
                <ProsConsItem type="con" text="Limited physical bookstore presence without extra effort" data-id="element-16737" data-mp="34j5lkia2rmjd" />
                <ProsConsItem type="con" text="Income is slow to build — often years before it becomes meaningful" data-id="element-16738" data-mp="vr7691l5px8k" />
              </div>
            </div>

            {/* Common Myths */}
            <div className="mb-2" data-id="element-16739" data-mp="rr6gikslxzm7">
              <h3 className={`${subHeadingClass}`} data-id="element-16740" data-mp="rs82vlf2q8j8">
                Common myths worth clearing up
              </h3>
              <p className={`${subtleText} text-sm mb-5`} data-id="element-16741" data-mp="1fh94aophojf0">
                These beliefs are understandable — but they set authors up for
                disappointment.
              </p>
            </div>
            <div className="space-y-3 mb-8" data-id="element-16742" data-mp="15i1pdx33wgps">
              {myths.map((item, idx) => <MythCard key={idx} myth={item.myth} reality={item.reality} data-id="element-16743" data-mp="1agczm8xbct8j" data-mpi={idx} />)}
            </div>
          </div>;
      case 'comparison':
        return <div data-id="element-16744" data-mp="3v85d1ud8jghk">
            <h2 className={headingClass} data-id="element-16745" data-mp="3v8okgh59emhc">Publishing Models</h2>
            <p className={`${subtleText} text-sm mb-8`} data-id="element-16746" data-mp="3no4bz1e9647i">
              Three paths to publication — each with different trade-offs on
              control, cost, and timeline.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8" data-id="element-16747" data-mp="jensqls1hqd">
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-5`} data-id="element-16748" data-mp="hk9hv50gnv9">
                <div className={`h-10 w-10 rounded-full ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'} flex items-center justify-center mb-4`} data-id="element-16749" data-mp="3416l8kiomtvy">
                  <BookIcon className="h-5 w-5 text-blue-600" data-id="element-16750" data-mp="3dqgdq1hfuekl" />
                </div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16751" data-mp="38zq7km16u7ou">
                  Traditional Publishing
                </h3>
                <p className={`text-sm ${subtleText} mb-4 leading-relaxed`} data-id="element-16752" data-mp="2gbcb4apqzjfe">
                  A publisher acquires your book, handles production, and
                  manages distribution. You receive an advance against future
                  royalties.
                </p>
                <ul className={`space-y-2 text-sm ${textClass}`} data-id="element-16753" data-mp="txhb08pnqhsf">
                  <li className="flex items-start gap-2" data-id="element-16754" data-mp="ty97rmuzqlka">
                    <span className="text-blue-400 mt-1" data-id="element-16755" data-mp="ywtxgscm9muh">—</span>
                    <span data-id="element-16756" data-mp="yyddqiy9vxw9">
                      Submit to agents or publishers; acceptance is competitive
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16757" data-mp="68od46ph5zv6">
                    <span className="text-blue-400 mt-1" data-id="element-16758" data-mp="b77xi2of73lf">—</span>
                    <span data-id="element-16759" data-mp="b92ljpuznbtq">
                      Publisher covers production costs; may offer an advance
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16760" data-mp="1mmwkgu1oc2m8">
                    <span className="text-blue-400 mt-1" data-id="element-16761" data-mp="1mohfekrwhnkm">—</span>
                    <span data-id="element-16762" data-mp="1qm5f014jg4iu">
                      Royalties typically 8–15%; publisher retains most rights
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16763" data-mp="374fmggubjxh6">
                    <span className="text-blue-400 mt-1" data-id="element-16764" data-mp="374z0vnkrg3qg">—</span>
                    <span data-id="element-16765" data-mp="32cfec143hy9i">
                      Timeline: 1–2+ years from acceptance to publication
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16766" data-mp="2xcjk2tl79l75">
                    <span className="text-blue-400 mt-1" data-id="element-16767" data-mp="2sg2k924n1ie8">—</span>
                    <span data-id="element-16768" data-mp="2nfooaigb9ywk">
                      Limited creative input on cover, title, and marketing
                    </span>
                  </li>
                </ul>
              </div>

              <div className={`${darkMode ? 'bg-gray-800 border-indigo-700/50' : 'bg-white border-indigo-200'} rounded-lg border-2 p-5`} data-id="element-16769" data-mp="2efrslwvi7zs0">
                <div className={`h-10 w-10 rounded-full ${darkMode ? 'bg-indigo-900/50' : 'bg-indigo-100'} flex items-center justify-center mb-4`} data-id="element-16770" data-mp="hl5g8umj2fy9">
                  <PenToolIcon className="h-5 w-5 text-indigo-600" data-id="element-16771" data-mp="35j5a2h9epc7b" />
                </div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16772" data-mp="3ah6oupzsupcz">
                  Self-Publishing
                </h3>
                <p className={`text-sm ${subtleText} mb-4 leading-relaxed`} data-id="element-16773" data-mp="31fgy7mdqrug9">
                  You are both author and publisher. You manage every aspect of
                  production and marketing, and you keep the majority of
                  revenue.
                </p>
                <ul className={`space-y-2 text-sm ${textClass}`} data-id="element-16774" data-mp="2ds8wfefiv3iz">
                  <li className="flex items-start gap-2" data-id="element-16775" data-mp="2ht15o7oe98f9">
                    <span className="text-indigo-400 mt-1" data-id="element-16776" data-mp="2htkk3eeu5eoj">—</span>
                    <span data-id="element-16777" data-mp="23t04er47vyz9">
                      Complete control over content, design, pricing, and
                      timeline
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16778" data-mp="1p6b2yjqenbq8">
                    <span className="text-indigo-400 mt-1" data-id="element-16779" data-mp="1p6t9qaap18ce">—</span>
                    <span data-id="element-16780" data-mp="1k8jczv9blzoo">
                      You cover all upfront costs for editing, design, and
                      formatting
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16781" data-mp="1u54hmfhfh8rz">
                    <span className="text-indigo-400 mt-1" data-id="element-16782" data-mp="1u7qoecwhvk95">—</span>
                    <span data-id="element-16783" data-mp="284471pcrlrp7">
                      Royalties 35–70% depending on platform and format
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16784" data-mp="2hv8l8mum98iz">
                    <span className="text-indigo-400 mt-1" data-id="element-16785" data-mp="2hup6tg46d29p">—</span>
                    <span data-id="element-16786" data-mp="2cvvqzh1f0z6d">
                      Publish as quickly as you can produce quality work
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16787" data-mp="2qtu3si038njr">
                    <span className="text-indigo-400 mt-1" data-id="element-16788" data-mp="2mvcrzl5q2ow0">—</span>
                    <span data-id="element-16789" data-mp="2txrr9ykh0zuz">All marketing is your responsibility</span>
                  </li>
                </ul>
              </div>

              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-5`} data-id="element-16790" data-mp="2yxnv9xt64raj">
                <div className={`h-10 w-10 rounded-full ${darkMode ? 'bg-amber-900/50' : 'bg-amber-100'} flex items-center justify-center mb-4`} data-id="element-16791" data-mp="38mfkhy6ugxhr">
                  <UsersIcon className="h-5 w-5 text-amber-600" data-id="element-16792" data-mp="3cq3vkquoqb7r" />
                </div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16793" data-mp="3hl9erhwpdbs5">
                  Hybrid Publishing
                </h3>
                <p className={`text-sm ${subtleText} mb-4 leading-relaxed`} data-id="element-16794" data-mp="3rkic8kqs18j9">
                  You pay a company to provide publishing services while
                  retaining more rights than traditional publishing. Quality
                  varies widely.
                </p>
                <ul className={`space-y-2 text-sm ${textClass}`} data-id="element-16795" data-mp="edpe0ka4z04v">
                  <li className="flex items-start gap-2" data-id="element-16796" data-mp="9kwce59ys7x0">
                    <span className="text-amber-400 mt-1" data-id="element-16797" data-mp="9jbhgejqmmym">—</span>
                    <span data-id="element-16798" data-mp="5km6esm69hi5">
                      You pay for services (editing, design, distribution)
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16799" data-mp="xb09ruwk7bfk">
                    <span className="text-amber-400 mt-1" data-id="element-16800" data-mp="xagvco64b56a">—</span>
                    <span data-id="element-16801" data-mp="t95eb71b7s9p">More creative input than traditional publishing</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16802" data-mp="o9iz4s1x81y9">
                    <span className="text-amber-400 mt-1" data-id="element-16803" data-mp="oc57gtwmylhj">—</span>
                    <span data-id="element-16804" data-mp="japiwrv91wue">
                      Some distribution and marketing support provided
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16805" data-mp="341q2b6x82uf9">
                    <span className="text-amber-400 mt-1" data-id="element-16806" data-mp="34055tbrcl1er">—</span>
                    <span data-id="element-16807" data-mp="17y214xum8w2a">
                      Royalties and rights vary significantly by contract
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-16808" data-mp="1cv1ymkdegqhs">
                    <span className="text-amber-400 mt-1" data-id="element-16809" data-mp="y1fkymd8it6l">—</span>
                    <span data-id="element-16810" data-mp="12yyw3demypub">
                      Caution: some are predatory vanity presses in disguise
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className={`${darkMode ? 'bg-amber-900/20 border-amber-800/40' : 'bg-amber-50 border-amber-200'} rounded-lg border p-5`} data-id="element-16811" data-mp="1vofq2fhwmesn">
              <div className="flex items-start gap-3" data-id="element-16812" data-mp="1gx5m67dcfljf">
                <AlertCircleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-16813" data-mp="1lws1cmcqfbuv" />
                <div data-id="element-16814" data-mp="29mmc8ydhqqch">
                  <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16815" data-mp="29m45h7t7ctqb">
                    A note on hybrid publishers
                  </h3>
                  <p className={`text-sm ${textClass} leading-relaxed`} data-id="element-16816" data-mp="2x288nln2ad5">
                    While some hybrid publishers offer genuine value, others
                    charge high fees for minimal return. Before signing
                    anything, research thoroughly, ask for client references,
                    and check industry watchdog resources like Writer Beware. If
                    a company approaches you unsolicited or guarantees sales,
                    treat that as a red flag.
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 'right-for-you':
        return <div data-id="element-16817" data-mp="3l3yn3whilmps">
            <h2 className={headingClass} data-id="element-16818" data-mp="3l3glslfzwib0">Is Self-Publishing Right for You?</h2>
            <p className={`${subtleText} text-sm mb-8`} data-id="element-16819" data-mp="14fcssj9t0l4n">
              An honest look at what the journey actually involves — practically
              and emotionally.
            </p>

            {/* What the first year looks like */}
            <div className="mb-8" data-id="element-16820" data-mp="pa021c7yiyhl">
              <h3 className={subHeadingClass} data-id="element-16821" data-mp="l6ln6ovr8o5q">
                What the first year actually looks like
              </h3>
              <p className={`${textClass} text-sm mb-5 leading-relaxed`} data-id="element-16822" data-mp="z74pdfdjgzzz">
                Most new authors underestimate how long the runway is. Here's a
                realistic picture — not to discourage you, but so you can plan
                properly.
              </p>
              <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`} data-id="element-16823" data-mp="145prjkccptr2">
                {[{
                phase: 'Writing a first draft',
                time: '6–18 months',
                note: 'Varies enormously by genre, word count, and how much time you can dedicate.'
              }, {
                phase: 'Editing and revision',
                time: '1–3 months',
                note: 'Developmental editing, line editing, and proofreading are separate stages — each takes time.'
              }, {
                phase: 'Cover design and formatting',
                time: '2–6 weeks',
                note: 'Working with a designer and formatter, plus revision rounds.'
              }, {
                phase: 'Launch and early sales',
                time: 'Ongoing',
                note: 'Most debut books sell slowly. Early sales are typically to your existing network — not strangers.'
              }, {
                phase: 'Building an audience',
                time: '1–3+ years',
                note: 'Meaningful readership usually comes after multiple books and consistent marketing effort.'
              }].map((item, idx) => <div key={idx} className="flex items-start gap-4 p-4" data-id="element-16824" data-mp="pplwkrjg0nqo" data-mpi={idx}>
                    <div className={`flex-shrink-0 text-xs font-medium px-2 py-1 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} whitespace-nowrap mt-0.5`} data-id="element-16825" data-mp="kydru8t25qju">
                      {item.time}
                    </div>
                    <div data-id="element-16826" data-mp="ayd3g8gc0k8s">
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16827" data-mp="18ohrk534l65k">
                        {item.phase}
                      </p>
                      <p className={`text-xs ${subtleText} mt-0.5`} data-id="element-16828" data-mp="yoqxg4ih8t60">
                        {item.note}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Cost expectations */}
            <div className="mb-8" data-id="element-16829" data-mp="3eh6by6u96iw3">
              <h3 className={subHeadingClass} data-id="element-16830" data-mp="3eg51a3h5fyew">Realistic cost expectations</h3>
              <p className={`${textClass} text-sm mb-5 leading-relaxed`} data-id="element-16831" data-mp="1tznfezkdng8j">
                Quality self-publishing requires upfront investment. These are
                rough ranges for a standard novel — costs vary by genre, length,
                and who you hire.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-id="element-16832" data-mp="28nibmpxlg2qd">
                {[{
                service: 'Developmental editing',
                range: '$500–$2,000',
                note: 'Big-picture story and structure feedback'
              }, {
                service: 'Copy editing / proofreading',
                range: '$200–$800',
                note: 'Line-level language and error correction'
              }, {
                service: 'Cover design',
                range: '$200–$800',
                note: 'Professional design for your genre'
              }, {
                service: 'Interior formatting',
                range: '$50–$300',
                note: 'Ebook and/or print layout'
              }, {
                service: 'ISBN & distribution setup',
                range: '$0–$150',
                note: 'Some platforms provide free ISBNs'
              }, {
                service: 'Marketing & advertising',
                range: 'Variable',
                note: 'From $0 (organic) to $500+/month for ads'
              }].map((item, idx) => <div key={idx} className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-4`} data-id="element-16833" data-mp="2dscbzvb26bbv" data-mpi={idx}>
                    <div className="flex items-start justify-between gap-2" data-id="element-16834" data-mp="1l9x7oivb2lr9">
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16835" data-mp="1hag0nl08dgjz">
                        {item.service}
                      </p>
                      <span className={`text-xs font-semibold ${accentText} whitespace-nowrap`} data-id="element-16836" data-mp="1bx7y3yr7c4ym">
                        {item.range}
                      </span>
                    </div>
                    <p className={`text-xs ${subtleText} mt-1`} data-id="element-16837" data-mp="124aaxfb9lrto">{item.note}</p>
                  </div>)}
              </div>
              <div className={`${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg p-4`} data-id="element-16838" data-mp="1uvk8fntunkvs">
                <p className={`text-sm ${textClass}`} data-id="element-16839" data-mp="1fxi3pab30tcy">
                  <span className="font-medium" data-id="element-16840" data-mp="1kwuqjv1ye0fj">
                    Rough total for a professionally produced debut:
                  </span>{' '}
                  £800–£3,500 before you earn a single sale. This is not unusual
                  — it is the cost of doing it properly.
                </p>
              </div>
            </div>

            {/* Income realism */}
            <div className="mb-8" data-id="element-16841" data-mp="2c6fp52bkoyib">
              <h3 className={subHeadingClass} data-id="element-16842" data-mp="2759v9166f577">Income realism</h3>
              <p className={`${textClass} text-sm mb-5 leading-relaxed`} data-id="element-16843" data-mp="277w5x9od60tn">
                This is the part most people don't talk about honestly. Here it
                is plainly.
              </p>
              <div className="space-y-3" data-id="element-16844" data-mp="1xdvyv29iulia">
                {[{
                icon: TrendingUpIcon,
                heading: 'Most debut authors earn under £500 in their first year',
                body: "This isn't a reflection of quality — it's a reflection of discoverability. A book with no existing audience and no marketing budget will reach very few readers, regardless of how good it is."
              }, {
                icon: BookIcon,
                heading: 'Your first book is rarely your best-earning book',
                body: 'Most indie authors see meaningful income only after 3–5 titles, because each new book sells the back catalogue. The first book is often the hardest and least profitable — and also the most important to write.'
              }, {
                icon: ClockIcon,
                heading: 'Full-time income typically takes 3–5+ years',
                body: "Authors who earn a living from self-publishing have usually built a back catalogue, an email list, and a marketing system over several years. It's achievable — but it's a long game, not a quick win."
              }, {
                icon: DollarSignIcon,
                heading: 'Recouping your investment takes time',
                body: 'At typical indie royalty rates, you might earn £2–£4 per ebook sale. Recovering a £1,500 production investment means selling 400–750 copies — which, for a debut author with no platform, can take a year or more.'
              }].map((item, idx) => <div key={idx} className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-4 flex items-start gap-4`} data-id="element-16845" data-mp="1cojvhst1rruc" data-mpi={idx}>
                    <div className={`h-9 w-9 rounded-full ${darkMode ? accentIconBg : accentBgLight} flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-16846" data-mp="1hpzgxlz500d9">
                      <item.icon className={`h-4 w-4 ${accentText}`} data-id="element-16847" data-mp="1rfspee3zfzda" />
                    </div>
                    <div data-id="element-16848" data-mp="25f1vum2dzf42">
                      <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`} data-id="element-16849" data-mp="25e8ww79o5twh">
                        {item.heading}
                      </p>
                      <p className={`text-sm ${textClass} leading-relaxed`} data-id="element-16850" data-mp="b1ss7awiuntq">
                        {item.body}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Emotional realities */}
            <div className="mb-8" data-id="element-16851" data-mp="2mnl022x4fakx">
              <h3 className={subHeadingClass} data-id="element-16852" data-mp="2mqp6z81z0kfa">The emotional side</h3>
              <p className={`${textClass} text-sm mb-5 leading-relaxed`} data-id="element-16853" data-mp="27yykc03l0gt6">
                The practical challenges are manageable with planning. The
                emotional ones are often harder to prepare for.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-16854" data-mp="1z14pyupdnx8n">
                {[{
                title: 'The comparison trap',
                body: "Social media shows you other authors' highlights — their launch days, their bestseller badges, their five-star reviews. It rarely shows the years of work, the quiet launches, or the books that didn't land."
              }, {
                title: 'Isolation',
                body: 'Writing and self-publishing can be solitary. Without colleagues or a built-in support system, the journey can feel lonely. Finding a community of fellow authors — even a small one — makes a significant difference.'
              }, {
                title: 'Burnout',
                body: 'Wearing every hat — writer, editor, marketer, business owner — while trying to stay creative is genuinely hard. Many indie authors hit burnout before they find a sustainable rhythm.'
              }].map((item, idx) => <div key={idx} className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-4`} data-id="element-16855" data-mp="1h5hdf2rb74l9" data-mpi={idx}>
                    <h4 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16856" data-mp="1q6yna3e4ia1i">
                      {item.title}
                    </h4>
                    <p className={`text-sm ${textClass} leading-relaxed`} data-id="element-16857" data-mp="3rz3jvs6khvik">
                      {item.body}
                    </p>
                  </div>)}
              </div>
            </div>

            {/* It's okay to say not now */}
            <div className={`${darkMode ? 'bg-green-900/20 border-green-800/40' : 'bg-green-50 border-green-200'} rounded-lg border p-6`} data-id="element-16858" data-mp="3aorehbjzo813">
              <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-3`} data-id="element-16859" data-mp="78slcqa8540c">
                It's okay to say "not now"
              </h3>
              <p className={`text-sm ${textClass} mb-3 leading-relaxed`} data-id="element-16860" data-mp="3tg0l9d2dkioz">
                There is no shame in deciding that self-publishing isn't right
                for you — either permanently or just at this point in your life.
                Many writers find deep satisfaction in creating stories without
                the pressure of publication. Others choose to wait until they
                have more time, resources, or emotional bandwidth.
              </p>
              <p className={`text-sm ${textClass} leading-relaxed`} data-id="element-16861" data-mp="2fecmwwi2l4yk">
                Your worth as a writer is not determined by how you share your
                work. The most important thing is that you make a choice that
                fits your life — not someone else's timeline.
              </p>
            </div>
          </div>;
      case 'stories':
        return <div data-id="element-16862" data-mp="1womugg1s4v9t">
            <h2 className={headingClass} data-id="element-16863" data-mp="1wnv0tarqt7m6">Stories from the Other Side</h2>
            <p className={`${subtleText} text-sm mb-8`} data-id="element-16864" data-mp="21i7k9kt7yixx">
              Honest accounts from indie authors — not just the wins, but the
              full picture.
            </p>

            <AuthorSpotlight name="Maya Chen" book="The Midnight Garden" image="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80" story="My book went viral on TikTok six months after release. Suddenly I was shipping signed copies around the world. It was a dream — but also overwhelming. I wasn't prepared for the pressure of a follow-up, or the anxiety of having an audience waiting." outcome="Viral success opened doors, but it also brought challenges I hadn't anticipated. I had to learn to set boundaries, manage expectations, and remember why I started writing. Success looks different than I imagined — both better and harder." data-id="element-16865" data-mp="1rrwfxx5mi7qi" />

            <AuthorSpotlight name="James Wilson" book="The Quantum Detective Series" image="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80" story="I never went viral. I built my readership book by book over four years. I started with 12 readers — mostly family — and now have a mailing list of 3,500 dedicated fans. Each release sells a little better than the last. It's not glamorous, but it's sustainable." outcome="Slow growth has real advantages. I've had time to improve my craft, learn the business side gradually, and build genuine relationships with readers. My income grows steadily, and I now write full-time — but it took four books and four years to get there." data-id="element-16866" data-mp="1d4ny4dht745k" />

            <AuthorSpotlight name="Elena Rodriguez" book="Abuela's Kitchen" image="https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80" story="I self-published a cookbook of my grandmother's recipes with family stories woven through it. It was never meant to be a bestseller — I created it to preserve our heritage. To my surprise, it found an audience beyond what I expected." outcome="It hasn't made me rich, but it's connected me with people who share my cultural background and others who simply love authentic family recipes. The joy of creating something meaningful that will outlive me is worth more than bestseller status." data-id="element-16867" data-mp="2tn3me8m42ohc" />

            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 mt-2`} data-id="element-16868" data-mp="15qnt6vzlz1zu">
              <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-4`} data-id="element-16869" data-mp="10xujdtpa93gw">
                What these stories have in common
              </h3>
              <div className="space-y-3" data-id="element-16870" data-mp="rwwgxfuht40f">
                {[{
                bold: 'Persistence matters more than a perfect launch.',
                text: 'Consistent effort over time yields results, even without viral moments.'
              }, {
                bold: 'Community is essential.',
                text: 'Every author mentioned the importance of connecting with other writers and readers — especially in the early, quiet years.'
              }, {
                bold: 'Success is self-defined.',
                text: "Bestseller status isn't the only valid goal. Personal fulfilment, creative expression, and connecting with readers are equally meaningful outcomes."
              }, {
                bold: 'The first book is the hardest.',
                text: 'Not just to write — but to sell. Most authors look back on their debut as the foundation, not the peak.'
              }].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-16871" data-mp="tfmo6dt3a7ds" data-mpi={idx}>
                    <div className={`h-5 w-5 rounded-full ${darkMode ? accentIconBg : accentBgLight} flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-16872" data-mp="fh55wvlydn39">
                      <StarIcon className={`h-3 w-3 ${accentText}`} data-id="element-16873" data-mp="306co4aw30sni" />
                    </div>
                    <p className={`text-sm ${textClass}`} data-id="element-16874" data-mp="348g3vbpsajel">
                      <span className="font-medium" data-id="element-16875" data-mp="345uywdu6vncm">{item.bold}</span>{' '}
                      {item.text}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>;
      case 'start-here':
        return <div data-id="element-16876" data-mp="36mcmqu1mohrc">
            <h2 className={headingClass} data-id="element-16877" data-mp="36krytn9tjx2g">Still Curious? Start Here.</h2>
            <p className={`${subtleText} text-sm mb-8`} data-id="element-16878" data-mp="31ob1q3kn68d8">
              If you've read this far and you're still interested, that's a
              meaningful signal. Here's how to take the next step without
              overwhelm.
            </p>

            {/* Story frameworks */}
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 mb-8`} data-id="element-16879" data-mp="2np1v9vm8msmg">
              <div className="flex items-start gap-3 mb-4" data-id="element-16880" data-mp="1v7phur3t55hr">
                <div className={`h-9 w-9 rounded-full ${darkMode ? accentIconBg : accentBgLight} flex items-center justify-center flex-shrink-0`} data-id="element-16881" data-mp="1v5uyhh86plfs">
                  <MapIcon className={`h-4 w-4 ${accentText}`} data-id="element-16882" data-mp="14ql5mu5k7a5d" />
                </div>
                <div data-id="element-16883" data-mp="19pfn9srsisnw">
                  <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`} data-id="element-16884" data-mp="19owf33rxzin2">
                    Your story probably already fits a proven structure
                  </h3>
                  <p className={`text-sm ${subtleText}`} data-id="element-16885" data-mp="1nofbx0faz7k2">
                    And that's a strength, not a limitation.
                  </p>
                </div>
              </div>
              <p className={`text-sm ${textClass} mb-4 leading-relaxed`} data-id="element-16886" data-mp="1skvz9seohhw3">
                Storytelling scholars have identified that most compelling
                narratives follow one of a handful of universal structures —
                Christopher Booker's seven basic plots (Overcoming the Monster,
                Rags to Riches, The Quest, Voyage and Return, Comedy, Tragedy,
                Rebirth), or Joseph Campbell's Hero's Journey, which underpins
                everything from ancient myth to modern blockbusters.
              </p>
              <p className={`text-sm ${textClass} leading-relaxed`} data-id="element-16887" data-mp="3qvmokuodfm2x">
                This doesn't mean your story is unoriginal. It means it's built
                on a foundation that resonates with readers at a deep level. The
                story in your head is yours — the structure is just the
                scaffolding that helps it stand.
              </p>
            </div>

            {/* Next steps */}
            <div className="mb-8" data-id="element-16888" data-mp="efsu0waus1fc">
              <h3 className={subHeadingClass} data-id="element-16889" data-mp="9okle2hckk38">A sensible starting point</h3>
              <div className="space-y-3" data-id="element-16890" data-mp="9mpw6bz1m5tc">
                {[{
                step: '1',
                title: 'Write the book first',
                desc: "Don't worry about publishing models, platforms, or marketing until you have a complete draft. Many aspiring authors get lost in research before they've written a word. The manuscript comes first."
              }, {
                step: '2',
                title: 'Read widely in your genre',
                desc: "The best preparation for writing is reading. Understanding what readers in your genre expect — and where there's room to surprise them — is more valuable than any course."
              }, {
                step: '3',
                title: 'Start building a small community',
                desc: "You don't need a large platform before you publish — but having even a small, engaged group of readers before launch makes a real difference. A newsletter with 200 genuine subscribers outperforms 10,000 social media followers."
              }, {
                step: '4',
                title: 'Treat it like a long game from day one',
                desc: 'Authors who succeed in self-publishing rarely do so on their first book. Plan for a series, a back catalogue, and a multi-year journey. That mindset changes how you make every decision along the way.'
              }].map((item, idx) => <div key={idx} className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-4 flex items-start gap-4`} data-id="element-16891" data-mp="c34284rm4vi8" data-mpi={idx}>
                    <div className={`h-7 w-7 rounded-full ${darkMode ? accentIconBg : accentBgLight} flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-16892" data-mp="3fkmc0hqy4cla">
                      <span className={`text-xs font-bold ${accentText}`} data-id="element-16893" data-mp="3pgpgvvd6mpbx">
                        {item.step}
                      </span>
                    </div>
                    <div data-id="element-16894" data-mp="14rt8ei7izr0u">
                      <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-1`} data-id="element-16895" data-mp="14qhvnsw3r8xl">
                        {item.title}
                      </p>
                      <p className={`text-sm ${textClass} leading-relaxed`} data-id="element-16896" data-mp="35s7mx6ujy6vj">
                        {item.desc}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* CTA */}
            <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-8 text-center`} data-id="element-16897" data-mp="39v4917jawl48">
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`} data-id="element-16898" data-mp="2i46jgcc8dgmz">
                Ready to move forward?
              </h3>
              <p className={`text-sm ${subtleText} mb-6 max-w-md mx-auto`} data-id="element-16899" data-mp="2vxfra8kj5u3e">
                The Author Launchpad is a practical toolkit for every stage of
                the self-publishing journey — from first draft to launch and
                beyond.
              </p>
              <Link to="/trove/author-launchpad" className={`inline-flex items-center px-6 py-3 ${buttonBg} text-white text-sm font-medium rounded-md transition-colors`} data-id="element-16900" data-mp="1yfxf73xexk74">
                Explore the Author Launchpad
                <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-16901" data-mp="4y9o4n3fzdmt" />
              </Link>
              <p className={`${subtleText} text-xs mt-4`} data-id="element-16902" data-mp="9wbe7rzacd7s">
                No pressure — explore at your own pace and decide if this path
                is right for you.
              </p>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <TroveLayout data-id="element-16903" data-mp="11leih7r69nkf">
      {/* Header */}
      <div className="mb-8" data-id="element-16904" data-mp="nmwpauihziv4">
        <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-16905" data-mp="nnpzk5go0qi0">
          Got a Story Stuck in Your Head?
        </h1>
        <p className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-16906" data-mp="38dff6kp70gbr">
          Thinking about becoming an author? Here's what self-publishing really
          involves — honestly, without the hype — so you can decide if this path
          is right for you.
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className={`sticky top-0 ${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'} border-b z-30 mb-8`} data-id="element-16907" data-mp="3jump0kst12xc">
        <div className="overflow-x-auto scrollbar-none" data-id="element-16908" data-mp="3ewtkdw75ggz6">
          <div className="flex min-w-max" data-id="element-16909" data-mp="fg51mlgk2ut8">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center px-4 py-4 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id ? activeTabClass + ' border-b-2' : `${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500'} ${hoverTextClass} border-b-2 border-transparent`}`} data-id="element-16910" data-mp="fe2dy82vldro">
                {cloneElement(tab.icon, {
              className: `h-4 w-4 ${activeTab === tab.id ? activeIconClass : 'text-gray-400'} mr-2`
            })}
                {tab.label}
              </button>)}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto" data-id="element-16911" data-mp="2gzo80vy5vuir">{renderTabContent()}</div>
    </TroveLayout>;
};
export default StoryInYourHead;